import React, { useRef, useState } from 'react'
import classNames from 'classnames'
import styles from './ImageLabelBoard.module.scss'

// Labels are stored in the legacy 900×500 coordinate space (learner view relies on it),
// the board only scales that space to the available width.
export const BOARD_W = 900
export const BOARD_H = 500
const LABEL_W = 200
const LABEL_H = 50
const MIN_LINE = 12

const pct = (value, total) => `${(value / total) * 100}%`

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

// Keeps the label (attached to the line end) fully inside the board
const fitEnd = (startX, endX, endY) => ({
  endX: startX <= endX ? clamp(endX, 0, BOARD_W - LABEL_W) : clamp(endX, LABEL_W, BOARD_W),
  endY: clamp(endY, LABEL_H / 2, BOARD_H - LABEL_H / 2),
})

// renderSlot(item, index): custom interactive content in place of the label text (learner view)
function ImageLabelBoard({ image, variants = [], editable = false, onAdd, emptyText, renderSlot }) {
  const svgRef = useRef(null)
  const [draft, setDraft] = useState(null)

  const toBoard = (e) => {
    const rect = svgRef.current.getBoundingClientRect()
    return {
      x: clamp(((e.clientX - rect.left) / rect.width) * BOARD_W, 0, BOARD_W),
      y: clamp(((e.clientY - rect.top) / rect.height) * BOARD_H, 0, BOARD_H),
    }
  }

  const canDraw = editable && Boolean(image)

  const onPointerDown = (e) => {
    if (!canDraw || e.button > 0) return
    e.currentTarget.setPointerCapture(e.pointerId)
    const { x, y } = toBoard(e)
    setDraft({ startX: x, startY: y, endX: x, endY: y })
  }

  const onPointerMove = (e) => {
    if (!draft) return
    const { x, y } = toBoard(e)
    setDraft({ ...draft, endX: x, endY: y })
  }

  const onPointerUp = () => {
    if (!draft) return
    const length = Math.hypot(draft.endX - draft.startX, draft.endY - draft.startY)
    if (length >= MIN_LINE) {
      onAdd({ startX: draft.startX, startY: draft.startY, ...fitEnd(draft.startX, draft.endX, draft.endY) })
    }
    setDraft(null)
  }

  const renderLabel = (item, index, isDraft) => {
    const left = item.startX > item.endX ? item.endX - LABEL_W : item.endX
    const style = {
      left: pct(left, BOARD_W),
      top: pct(item.endY - LABEL_H / 2, BOARD_H),
      width: pct(LABEL_W, BOARD_W),
      height: pct(LABEL_H, BOARD_H),
    }
    if (renderSlot && !isDraft) {
      return (
        <div key={item._id || index} className={styles.slot} style={style}>
          {renderSlot(item, index)}
        </div>
      )
    }
    return (
      <div
        key={isDraft ? 'draft' : item._id || index}
        className={classNames(styles.label, { [styles.draft]: isDraft })}
        style={style}
        title={item.value || ''}
      >
        {!isDraft && editable && <span className={styles.number}>{index + 1}</span>}
        {!isDraft && <span className={styles.text}>{item.value}</span>}
      </div>
    )
  }

  const draftFitted = draft && { ...draft, ...fitEnd(draft.startX, draft.endX, draft.endY) }

  return (
    <div
      className={classNames(styles.ImageLabelBoard, { [styles.editable]: canDraw })}
      style={image ? { backgroundImage: `url(${image})` } : undefined}
    >
      {!image && emptyText && <div className={styles.empty}>{emptyText}</div>}
      <svg
        ref={svgRef}
        className={styles.lines}
        viewBox={`0 0 ${BOARD_W} ${BOARD_H}`}
        preserveAspectRatio="none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => setDraft(null)}
      >
        {[...variants, ...(draftFitted ? [draftFitted] : [])].map((item, index) => (
          <g key={item._id || 'line-' + index}>
            <line x1={item.startX} y1={item.startY} x2={item.endX} y2={item.endY} className={styles.line} />
            <circle cx={item.startX} cy={item.startY} r="6" className={styles.dot} />
          </g>
        ))}
      </svg>
      {variants.map((item, index) => renderLabel(item, index, false))}
      {draftFitted && renderLabel(draftFitted, variants.length, true)}
    </div>
  )
}

export default ImageLabelBoard
